import { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import TicketsPage from "./TicketsPage";
import MisTicketsPage from "./MisTicketsPage";
import CategoriasTicketPage from "./CategoriasTicketPage";
import PrioridadesTicketPage from "./PrioridadesTicketPage";
import PlantillasTicketsPage from "./PlantillasTicketsPage";
import HallazgosCerradoresTab from "./HallazgosCerradoresTab";
import HallazgosCalendarioTab from "./HallazgosCalendarioTab";
import HallazgosTrazabilidadTab from "./HallazgosTrazabilidadTab";
import { Ticket, MessageSquare, Building2, AlertCircle, FileStack, FileSpreadsheet, Loader2, ShieldCheck, Lock, CalendarDays, GitBranch } from "lucide-react";

const MODULO = "hallazgos_auditoria";
const TABS_SOLO_ADMIN = ["categorias", "prioridades", "plantillas", "cierre"];

const TABS = [
  { id: "tickets", label: "Tickets", icon: Ticket },
  { id: "mis-tickets", label: "Mis Tickets", icon: MessageSquare },
  { id: "categorias", label: "Departamento a asignar", icon: Building2 },
  { id: "prioridades", label: "Prioridades", icon: AlertCircle },
  { id: "plantillas", label: "Plantillas", icon: FileStack },
  { id: "calendario", label: "Calendario", icon: CalendarDays },
  { id: "trazabilidad", label: "Trazabilidad", icon: GitBranch },
  { id: "cierre", label: "Cierre Definitivo", icon: ShieldCheck },
];

export default function HallazgosAuditoriaPage() {
  const { getAuthHeaders, user } = useAuth();
  const { ticketId } = useParams();
  const [tab, setTab] = useState("tickets");
  const [abrirTicket, setAbrirTicket] = useState(ticketId ? { id: ticketId } : null);
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [descargando, setDescargando] = useState(false);

  const esAdmin = ["administrador", "admin", "superadmin"].includes((user?.role || "").toLowerCase());
  const tieneAcceso = esAdmin || user?.menu_permissions?.hallazgos_auditoria?.access === true;

  if (user && !tieneAcceso) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 flex items-center justify-center p-8" data-testid="hallazgos-acceso-denegado">
          <div className="text-center max-w-md">
            <Lock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">Acceso restringido</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No tienes permiso para acceder a Hallazgos Auditoria. Solicita el acceso a un administrador.
            </p>
          </div>
        </main>
      </div>
    );
  }

  const descargarReporte = async () => {
    if (!mes) {
      toast.error("Selecciona un mes");
      return;
    }
    try {
      setDescargando(true);
      const res = await axios.get(`${API}/hallazgos-auditoria/reporte-mensual?mes=${mes}`, {
        ...getAuthHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_hallazgos_auditoria_${mes}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Reporte descargado");
    } catch {
      toast.error("No se pudo descargar el reporte");
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 pt-14 md:pt-4" data-testid="hallazgos-auditoria-page">
        <div className="px-4 md:px-8 border-b border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="flex gap-1 overflow-x-auto">
            {TABS.filter((t) => esAdmin || !TABS_SOLO_ADMIN.includes(t.id)).map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap -mb-px border-b-2 transition-colors ${
                  tab === t.id
                    ? "border-[#0C314C] text-[#0C314C] dark:border-blue-400 dark:text-blue-400"
                    : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                }`}
                data-testid={`hallazgos-tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pb-2 md:pb-1">
            <input
              type="month"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
              className="h-8 px-2 text-xs border rounded-md bg-white dark:bg-slate-800 dark:border-slate-600 text-slate-700 dark:text-slate-200"
              data-testid="hallazgos-reporte-mes"
            />
            <Button
              size="sm"
              onClick={descargarReporte}
              disabled={descargando}
              className="h-8 bg-[#0C314C] hover:bg-[#2a4d7a] text-white text-xs"
              data-testid="hallazgos-reporte-descargar-btn"
            >
              {descargando ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />}
              Reporte Mensual
            </Button>
          </div>
        </div>
        {tab === "tickets" && <TicketsPage modulo={MODULO} embedded abrirTicket={abrirTicket} />}
        {tab === "mis-tickets" && <MisTicketsPage modulo={MODULO} embedded />}
        {tab === "categorias" && esAdmin && <CategoriasTicketPage modulo={MODULO} embedded />}
        {tab === "prioridades" && esAdmin && <PrioridadesTicketPage modulo={MODULO} embedded />}
        {tab === "plantillas" && esAdmin && <PlantillasTicketsPage modulo={MODULO} embedded />}
        {tab === "calendario" && <HallazgosCalendarioTab onOpenTicket={(id) => { setAbrirTicket({ id }); setTab("tickets"); }} />}
        {tab === "trazabilidad" && <HallazgosTrazabilidadTab />}
        {tab === "cierre" && esAdmin && <HallazgosCerradoresTab />}
      </main>
    </div>
  );
}
